import { config } from '@/config';
import { useAuthStore } from '@/stores/authStore';
import { errorReporter } from '@/services/errorReportingService';
import { FormatType, NoteFormatResponse } from '../types';

const BASE_ENDPOINT = '/note-formats';

async function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const { userToken } = useAuthStore.getState();

  if (!userToken) {
    throw new Error('User not authenticated');
  }

  return fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${userToken}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
}

export const fetchFormatTypes = async (): Promise<FormatType[]> => {
  const response = await authenticatedFetch(`${config.apiUrl}${BASE_ENDPOINT}/types`);

  if (!response.ok) {
    errorReporter.critical('Failed to fetch format types', new Error(`HTTP ${response.status}`), {
      feature: 'noteFormats',
      operation: 'fetchTypes',
    });
    throw new Error('FETCH_FORMAT_TYPES_FAILED');
  }

  return response.json().catch((parseError: unknown) => {
    errorReporter.critical('Format types response was not valid JSON', parseError, {
      feature: 'noteFormats',
      operation: 'parseFormatTypes',
    });
    throw new Error('FETCH_FORMAT_TYPES_FAILED');
  });
};

export const generateNoteFormat = async ({
  noteId,
  promptId,
}: {
  noteId: string;
  promptId: string;
}): Promise<NoteFormatResponse> => {
  const response = await authenticatedFetch(`${config.apiUrl}${BASE_ENDPOINT}/generate`, {
    method: 'POST',
    body: JSON.stringify({ note_id: noteId, prompt_id: promptId }),
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('BACKEND_NOT_AVAILABLE');
    }

    if (response.status === 401) {
      errorReporter.critical('Note format auth token expired or invalid', new Error(`HTTP ${response.status}`), {
        feature: 'noteFormats',
        operation: 'generate',
      });
      throw new Error('Authentication expired. Please refresh the page and try again.');
    }

    errorReporter.critical('Note format generation API request failed', new Error(`HTTP ${response.status}`), {
      feature: 'noteFormats',
      operation: 'generate',
      httpStatus: response.status,
    });

    throw new Error('NOTE_FORMAT_GENERATION_FAILED');
  }

  const data = await response.json().catch((parseError: unknown) => {
    errorReporter.critical('Note format response body was not valid JSON', parseError, {
      feature: 'noteFormats',
      operation: 'parseResponse',
    });
    throw new Error('NOTE_FORMAT_GENERATION_FAILED');
  });

  if (!data?.id || !data?.prompt_result) {
    errorReporter.critical('Note format response missing required fields', new Error('Invalid response shape'), {
      feature: 'noteFormats',
      operation: 'validateResponse',
    });
    throw new Error('NOTE_FORMAT_GENERATION_FAILED');
  }

  return data as NoteFormatResponse;
};

export const deleteNoteFormat = async (noteId: string, formatId: string): Promise<void> => {
  const response = await authenticatedFetch(`${config.apiUrl}${BASE_ENDPOINT}/${noteId}/${formatId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    errorReporter.critical('Failed to delete note format', new Error(`HTTP ${response.status}`), {
      feature: 'noteFormats',
      operation: 'deleteFormat',
      noteId,
      formatId,
    });
    throw new Error('DELETE_NOTE_FORMAT_FAILED');
  }
};

export const fetchNoteFormats = async (noteId: string): Promise<NoteFormatResponse[]> => {
  const response = await authenticatedFetch(`${config.apiUrl}${BASE_ENDPOINT}/${noteId}`);

  if (!response.ok) {
    // 401/403 are expected on public/shared pages where the user may lack access
    const isAuthError = response.status === 401 || response.status === 403;
    const reporter = isAuthError ? errorReporter.expected : errorReporter.critical;
    reporter('Failed to fetch note formats', new Error(`HTTP ${response.status}`), {
      feature: 'noteFormats',
      operation: 'fetchFormats',
      noteId,
    });
    throw new Error('FETCH_NOTE_FORMATS_FAILED');
  }

  return response.json().catch((parseError: unknown) => {
    errorReporter.critical('Note formats response was not valid JSON', parseError, {
      feature: 'noteFormats',
      operation: 'parseNoteFormats',
      noteId,
    });
    throw new Error('FETCH_NOTE_FORMATS_FAILED');
  });
};

export const fetchSharedNoteFormats = async (token: string): Promise<NoteFormatResponse[]> => {
  const response = await fetch(`${config.apiUrl}/shared/${token}/formats`);

  if (!response.ok) {
    errorReporter.expected('Failed to fetch shared note formats', new Error(`HTTP ${response.status}`), {
      feature: 'noteFormats',
      operation: 'fetchSharedFormats',
      tokenPrefix: token.slice(0, 8),
    });
    throw new Error('FETCH_SHARED_NOTE_FORMATS_FAILED');
  }

  return (response.json() as Promise<NoteFormatResponse[]>).catch((parseError: unknown) => {
    errorReporter.critical('Shared note formats response was not valid JSON', parseError, {
      feature: 'noteFormats',
      operation: 'parseSharedNoteFormats',
      tokenPrefix: token.slice(0, 8),
    });
    throw new Error('FETCH_SHARED_NOTE_FORMATS_FAILED');
  });
};
